import api from './api';

export interface Cartera {
    nroBanco: number;
    nombreBanco: string;
    tipoDeCuenta?: string;
    nroDeCuenta?: string;
    cbu?: string;
    titular?: string;
    sucursal?: string;
    nroCtaCble?: number;
    mcpPermitidos1?: number;
    mcpPermitidos2?: number;
    mcpPermitidos3?: number;
    mcpPermitidos4?: number;
    mcpPermitidos5?: number;
    mcpPermitidos6?: number;
    mcpPermitidos7?: number;
    mcpPermitidos8?: number;
    mcpPermitidos9?: number;
    mcpPermitidos10?: number;
    [key: string]: any; // Para campos adicionales que puedan venir del backend
}

export interface CarteraSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
}

export interface CarteraPagedResult {
    items: Cartera[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

export interface SaldoCartera {
    nroBanco: number;
    nombreBanco: string;
    tipoDeCuenta: string;
    nroDeCuenta: string;
    cbu: string;
    titular: string;
    sucursal: string;
    nroCtaCble: number;
    mcpPermitidos1: number;
    mcpPermitidos2: number;
    mcpPermitidos3: number;
    mcpPermitidos4: number;
    mcpPermitidos5: number;
    mcpPermitidos6: number;
    mcpPermitidos7: number;
    mcpPermitidos8: number;
    mcpPermitidos9: number;
    mcpPermitidos10: number;
    saldo: number;
    saldoDebe: number;
    saldoHaber: number;
}

export interface SaldosCarteraSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    tipoDeCuenta?: string;
}

export interface SaldosCarteraResponse {
    items: SaldoCartera[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

export interface MovimientoCartera {
    nroFormulario: number;
    nroTipoFormulario: string;
    nombreTipoFormulario: string;
    nroSuc: number;
    fechaEmision: string;
    codigoCaja: number;
    usuario: string;
    nombre: string;
    descripcion: string;
    letra: string;
    nroComprobante: number;
    letraSucNum: string;
    debe?: number;
    haber?: number;
    saldoAcumulado?: number;
    mcp?: string;
    tipoPago?: string;
    detalle?: string;
    importe?: number;
    nroCuenta?: number;
    ingresoEgreso?: string;
}

export interface MovimientosCarteraSearchParams {
    pageNumber: number;
    pageSize: number;
    fechaDesde?: string;
    fechaHasta?: string;
    searchTerm?: string;
}

export interface MovimientosCarteraResponse {
    items: MovimientoCartera[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

const carteraService = {
    getCarteras: async (params: CarteraSearchParams): Promise<CarteraPagedResult> => {
        const q: Record<string, string | number> = {
            PageNumber: params.pageNumber,
            PageSize: params.pageSize,
        };
        const st = params.searchTerm?.trim();
        if (st) q.SearchTerm = st;
        const response = await api.get<CarteraPagedResult>('/api/CarteraBancos', { params: q });
        return response.data;
    },

    getCartera: async (nroBanco: number): Promise<Cartera> => {
        const response = await api.get<Cartera>(`/api/CarteraBancos/${nroBanco}`);
        return response.data;
    },

    createCartera: async (cartera: Cartera): Promise<Cartera> => {
        const response = await api.post<Cartera>('/api/CarteraBancos', cartera);
        return response.data;
    },

    updateCartera: async (nroBanco: number, cartera: Cartera): Promise<void> => {
        await api.put(`/api/CarteraBancos/${nroBanco}`, cartera);
    },

    deleteCartera: async (nroBanco: number): Promise<void> => {
        await api.delete(`/api/CarteraBancos/${nroBanco}`);
    },

    getSaldos: async (params?: SaldosCarteraSearchParams): Promise<SaldosCarteraResponse> => {
        const response = await api.get<SaldosCarteraResponse>('/api/CarteraBancos/saldos', {
            params: params ? {
                SearchTerm: params.searchTerm,
                PageNumber: params.pageNumber,
                PageSize: params.pageSize,
                TipoDeCuenta: params.tipoDeCuenta
            } : undefined
        });
        return response.data;
    },

    getMovimientos: async (nroBanco: number, params: MovimientosCarteraSearchParams): Promise<MovimientosCarteraResponse> => {
        const response = await api.get<MovimientosCarteraResponse>(`/api/CarteraBancos/${nroBanco}/movimientos`, {
            params: {
                PageNumber: params.pageNumber,
                PageSize: params.pageSize,
                FechaDesde: params.fechaDesde,
                FechaHasta: params.fechaHasta,
                SearchTerm: params.searchTerm
            }
        });
        return response.data;
    },

    /** Todas las carteras bancarias (paginado en cliente). Usado p. ej. en Conciliación bancaria. */
    getCarteraBancosTodos: async (): Promise<Cartera[]> => {
        const allItems: Cartera[] = [];
        let pageNumber = 1;
        const pageSize = 20;
        let hasMore = true;
        while (hasMore) {
            const result = await carteraService.getCarteras({
                pageNumber,
                pageSize,
            });
            if (result.items?.length) allItems.push(...result.items);
            const totalCount = result.totalCount ?? 0;
            const totalPages = result.totalPages ?? 1;
            if (!result.items?.length || allItems.length >= totalCount || pageNumber >= totalPages) {
                hasMore = false;
            } else {
                pageNumber += 1;
            }
        }
        return allItems;
    },

    getMovimientosConciliacion: async (
        nroBanco: number,
        opts?: { pagination?: { pageNumber: number; pageSize: number }; fechaDesde?: string; fechaHasta?: string }
    ): Promise<{ items: MovimientoCartera[]; totalCount?: number; totalPages?: number }> => {
        const params: Record<string, number | string> = { nroBanco };
        if (opts?.pagination) {
            params.PageNumber = opts.pagination.pageNumber;
            params.PageSize = opts.pagination.pageSize;
        }
        if (opts?.fechaDesde) params.FechaDesde = opts.fechaDesde;
        if (opts?.fechaHasta) params.FechaHasta = opts.fechaHasta;
        const response = await api.get<any>('/api/ConciliacionBancaria/movimientos-cartera', { params });
        const data = response.data;
        const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        const totalCount = data?.totalCount ?? data?.total;
        const totalPages = data?.totalPages;
        return { items, totalCount, totalPages };
    },

    getMovimientosConciliacionTodos: async (
        nroBanco: number,
        filtros?: { fechaDesde?: string; fechaHasta?: string }
    ): Promise<MovimientoCartera[]> => {
        try {
            const allItems: MovimientoCartera[] = [];
            let pageNumber = 1;
            const pageSize = 20;
            let hasMore = true;
            while (hasMore) {
                const result = await carteraService.getMovimientosConciliacion(nroBanco, {
                    pagination: { pageNumber, pageSize },
                    fechaDesde: filtros?.fechaDesde,
                    fechaHasta: filtros?.fechaHasta,
                });
                if (result.items?.length) allItems.push(...result.items);
                const totalCount = result.totalCount ?? 0;
                const totalPages = result.totalPages ?? 1;
                if (!result.items?.length || allItems.length >= totalCount || pageNumber >= totalPages) {
                    hasMore = false;
                } else {
                    pageNumber += 1;
                }
            }
            return allItems;
        } catch {
            const res = await carteraService.getMovimientosConciliacion(nroBanco, {
                fechaDesde: filtros?.fechaDesde,
                fechaHasta: filtros?.fechaHasta,
            });
            return res.items || [];
        }
    },
};

export default carteraService;

